import React, { useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import { Container } from '@mui/material';
import toast from 'react-hot-toast';
import { useAssessment } from '../contexts/AssessmentContext';
import { useLocale } from '../contexts/LocaleContext';
import { assessmentAPI, sessionAPI } from '../services/api';
import SingleQuestionPage from '../components/Assessment/SingleQuestionPage';
import { trackSessionCreated, trackCampaignSource, setSessionId, setUserId } from '../utils/analytics';
// Bundled assessment data - questions show instantly!
import assessmentData from '../data/360-questions.json';

// Capture and store campaign params from URL
const captureCampaignParams = () => {
  const params = new URLSearchParams(window.location.search);
  const email = params.get('uemail');
  const campaign = params.get('utm_campaign');
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');

  // Store if we have campaign data
  if (email || campaign || source) {
    if (email) sessionStorage.setItem('campaign_email', email);
    if (campaign) sessionStorage.setItem('utm_campaign', campaign);
    if (source) sessionStorage.setItem('utm_source', source);
    if (medium) sessionStorage.setItem('utm_medium', medium);

    // Track campaign visit in GA
    trackCampaignSource({ email: email || undefined, campaign: campaign || undefined, source: source || undefined, medium: medium || undefined });

    // Clean email from URL (keep UTM params for GA)
    if (email) {
      params.delete('uemail');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }
};

const AssessmentPage: React.FC = () => {
  const { type } = useParams<{ type: '360' }>();
  const navigate = useNavigate();
  const { state, dispatch } = useAssessment();
  const { t, language } = useLocale();
  const initStartedRef = useRef(false);
  const assessmentIdRef = useRef<string | null>(null);
  const campaignCapturedRef = useRef(false);

  // Transform bundled data - only include what frontend needs (no scoring data)
  const assessment = useMemo(() => {
    const data = assessmentData.assessment;
    const scoringOptions = data.scoringSystem.options;

    // Build questions array with only client-needed fields
    const questions = data.pages.map((page: any) => ({
      page: page.page,
      questions: page.questions.map((q: any) => ({
        id: q.id,
        text: q.text[language] || q.text,
        type: q.type,
        required: q.required,
        // Only include option values and labels (no scores)
        options: q.type === 'radio' ? scoringOptions.map((opt: any) => ({
          value: opt.value,
          label: opt.label[language] || opt.label,
        })) : undefined,
      })),
    }));

    return {
      type: data.type,
      title: data.title[language] || data.title,
      description: data.description[language] || data.description,
      totalQuestions: data.totalQuestions,
      questions,
    };
  }, [language]);

  // Create session mutation
  const createSessionMutation = useMutation(
    (params: { assessmentId: string; email?: string }) =>
      sessionAPI.create({
        assessmentId: params.assessmentId,
        participantEmail: params.email,
      }),
    {
      onSuccess: async (response: any, variables) => {
        const session = response.data;
        const sessionId = session.id || session.sessionId;

        // Set session ID for all future GA events
        setSessionId(sessionId);

        // Set user_id if we have email
        if (variables.email) {
          await setUserId(variables.email);
        }

        dispatch({ type: 'SET_SESSION_ID', payload: sessionId });
        dispatch({ type: 'SET_CURRENT_PAGE', payload: 1 });
        console.log('Session created in background:', sessionId);
        trackSessionCreated(sessionId, type!);
      },
      onError: (error) => {
        console.error('Failed to create session:', error);
        toast.error(t('toast.session.error'));
      },
    }
  );

  // Capture campaign params on mount (once)
  useEffect(() => {
    if (!campaignCapturedRef.current) {
      campaignCapturedRef.current = true;
      captureCampaignParams();
    }
  }, []);

  useEffect(() => {
    if (!type || type !== '360') {
      navigate('/');
      return;
    }

    // Reset state when starting a new assessment
    if (!state.currentSessionId) {
      dispatch({ type: 'RESET_STATE' });
      dispatch({ type: 'SET_ASSESSMENT_TYPE', payload: type });
    }
  }, [type, navigate, dispatch, state.currentSessionId]);

  // Fetch assessment ID and create session in background
  useEffect(() => {
    if (initStartedRef.current || state.currentSessionId) return;
    initStartedRef.current = true;

    // Fetch real assessment ID from API, then create session
    const initSession = async () => {
      try {
        const response = await assessmentAPI.getByType(type as '360', language);
        const assessments = response.data as any[];
        if (assessments && assessments.length > 0) {
          assessmentIdRef.current = assessments[0].id;
          // Get campaign email if exists
          const campaignEmail = sessionStorage.getItem('campaign_email') || undefined;
          createSessionMutation.mutate({
            assessmentId: assessments[0].id,
            email: campaignEmail,
          });
        }
      } catch (error) {
        console.error('Failed to fetch assessment:', error);
        toast.error(t('toast.session.error'));
      }
    };

    initSession();
  }, [type, language, state.currentSessionId, createSessionMutation, t]);

  const handleSessionComplete = (sessionId: string) => {
    navigate(`/results/${sessionId}`);
  };

  // Show questions immediately - session creates in background
  return (
    <Container maxWidth="md">
      <SingleQuestionPage
        assessment={assessment}
        sessionReady={!!state.currentSessionId}
        onComplete={() => {
          if (state.currentSessionId) {
            handleSessionComplete(state.currentSessionId);
          }
        }}
      />
    </Container>
  );
};

export default AssessmentPage;